<app-main-layout>
  <div class="px-6 py-6 bg-gray-50 min-h-screen">
    <!-- Top bar -->
    <div class="flex flex-row justify-between items-center mb-4">
      <button
        class="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
        (click)="backToQueue()"
        >
        <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">arrow_back</mat-icon>
        Back to Queue
      </button>

      @if (!loading && order) {
        <div class="flex items-center gap-x-2">
          @if (canOpenSale) {
            <a
              class="flex items-center gap-1.5 h-9 px-4 rounded-xl border border-amber-300 bg-white text-sm font-semibold text-amber-700 hover:bg-amber-400 hover:text-neutral-900 hover:border-amber-400 shadow-sm transition-colors"
              [routerLink]="['/sales', order.orderNo]"
              matTooltip="Open this order on the sales page"
              >
              <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">receipt_long</mat-icon>
              View sale
            </a>
          }
          <div class="flex items-center rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
            <button
              class="flex items-center justify-center h-9 w-9 text-gray-500 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
              (click)="goToPrevious()"
              [disabled]="!previousOrderId"
              matTooltip="Previous order in queue"
              >
              <mat-icon class="!text-[18px]">chevron_left</mat-icon>
            </button>
            <span class="w-px h-5 bg-gray-200"></span>
            <span
              class="px-3 text-xs font-semibold text-gray-500 whitespace-nowrap"
              [matTooltip]="queuePosition === -1 ? 'This order is not in any queue tab, so there is no next or previous' : 'Position in: ' + queueTabLabel"
              >
              {{ queuePosition === -1 ? '-' : queuePosition + 1 }} / {{ queueOrders.length || '-' }}
            </span>
            <span class="w-px h-5 bg-gray-200"></span>
            <button
              class="flex items-center justify-center h-9 w-9 text-gray-500 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
              (click)="goToNext()"
              [disabled]="!nextOrderId"
              matTooltip="Next order in queue"
              >
              <mat-icon class="!text-[18px]">chevron_right</mat-icon>
            </button>
          </div>
          <button
            class="flex items-center gap-1.5 h-9 px-4 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 shadow-sm transition-colors"
            [matMenuTriggerFor]="actionsMenu"
            >
            Actions
            <mat-icon class="!text-[18px]">expand_more</mat-icon>
          </button>
          <mat-menu #actionsMenu="matMenu">
            @if (shippingAddress) {
              <a mat-menu-item [href]="mapsUrl(shippingAddress)" target="_blank" rel="noopener">
                <mat-icon>map</mat-icon>
                <span>Open address in Maps</span>
              </a>
            }
            <button mat-menu-item (click)="printDeliverySlip()">
              <mat-icon>print</mat-icon>
              <span>Print Delivery Slip</span>
            </button>
          </mat-menu>
        </div>
      }
    </div>

    <!-- Loading -->
    @if (loading) {
      <div class="flex justify-center py-24">
        <mat-spinner diameter="36"></mat-spinner>
      </div>
    }

    <!-- Not found -->
    @if (!loading && notFound) {
      <div class="flex flex-col items-center py-24 text-gray-400">
        <mat-icon class="!text-4xl !h-10 !w-10 mb-2 opacity-40">search_off</mat-icon>
        <div>Order not found.</div>
      </div>
    }

    @if (!loading && order) {
      <!-- Header card -->
      <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
        <div class="flex flex-row justify-between items-start flex-wrap gap-y-4">
          <div>
            <div class="flex items-center gap-3">
              <div class="flex items-center justify-center h-12 w-12 rounded-2xl bg-neutral-900 text-amber-400 font-bold text-sm flex-shrink-0">
                #{{ order.orderNo?.toString()?.slice(-4) }}
              </div>
              <div>
                <div class="flex flex-wrap items-center gap-2">
                  <div class="text-xl font-bold text-gray-900">Order #{{ order.orderNo }}</div>
                  @if (order.paymentMethod) {
                    <span class="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize">
                      <mat-icon class="!text-[13px] !h-[13px] !w-[13px]">credit_card</mat-icon>{{ order.paymentMethod | lowercase }}
                    </span>
                  }
                  @if (order.paymentDate) {
                    <span class="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                      <mat-icon class="!text-[13px] !h-[13px] !w-[13px]">check_circle</mat-icon>Paid {{ order.paymentDate | date: 'dd MMM' }}
                    </span>
                  }
                  <button class="text-gray-300 hover:text-gray-700 transition-colors" (click)="copyText(order.orderNo, 'Order number')" matTooltip="Copy order number">
                    <mat-icon class="!text-[16px] !h-4 !w-4">content_copy</mat-icon>
                  </button>
                </div>
                <div class="text-sm text-gray-400">Placed {{ order.orderDate | date: 'dd MMM yyyy, HH:mm' }}</div>
              </div>
            </div>
            <!-- To pack -->
            <div class="mt-4 flex flex-wrap gap-3">
              <div class="rounded-xl border border-gray-100 bg-gray-50 px-4 py-2.5">
                <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Lines</div>
                <div class="text-lg font-bold text-gray-900">{{ activeLineCount }}</div>
              </div>
              <div class="rounded-xl border border-gray-100 bg-gray-50 px-4 py-2.5">
                <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Units to pack</div>
                <div class="text-lg font-bold text-gray-900">{{ unitsToPack }}</div>
              </div>
              @if (totalWeightKg) {
                <div class="rounded-xl border border-gray-100 bg-gray-50 px-4 py-2.5">
                  <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Approx. weight</div>
                  <div class="text-lg font-bold text-gray-900">{{ totalWeightKg | number:'1.0-0' }} kg</div>
                </div>
              }
            </div>
            <div class="mt-4 flex flex-row flex-wrap gap-x-8 gap-y-3 text-sm">
              <div>
                <div class="text-gray-400 text-xs uppercase tracking-wide mb-0.5">Customer</div>
                <div class="text-gray-800 font-medium">{{ customerName }}</div>
              </div>
              @if (order.deliveryMethod) {
                <div>
                  <div class="text-gray-400 text-xs uppercase tracking-wide mb-0.5">Delivery Method</div>
                  <div
                    class="flex items-center gap-1 font-medium"
                    [ngClass]="order.deliveryMethod === 'COLLECTION' ? 'text-purple-700' : 'text-blue-700'"
                    >
                    <mat-icon class="!text-[16px] !h-4 !w-4">{{ deliveryMethodIcon }}</mat-icon>
                    {{ deliveryMethodLabel }}
                  </div>
                </div>
              }
              @if (deliveryTermsShort) {
                <div>
                  <div class="text-gray-400 text-xs uppercase tracking-wide mb-0.5">Delivery Terms</div>
                  <div class="flex items-center gap-1 text-amber-700 font-medium" [matTooltip]="order.deliveryTerms">
                    <mat-icon class="!text-[16px] !h-4 !w-4">local_shipping</mat-icon>
                    {{ deliveryTermsShort }}
                  </div>
                </div>
              }
              @if (dispatchDate; as dDate) {
                <div>
                  <div class="text-gray-400 text-xs uppercase tracking-wide mb-0.5">Dispatch By</div>
                  <div class="inline-flex items-center gap-1 font-medium px-2 py-0.5 rounded-lg" [ngClass]="dispatchUrgencyClass">
                    <mat-icon class="!text-[16px] !h-4 !w-4">event</mat-icon>
                    {{ dDate | date: 'dd MMM yyyy' }}
                  </div>
                </div>
              }
            </div>
          </div>
          <div class="flex flex-col items-end gap-y-2">
            <button
              class="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all"
              [ngClass]="orderStatusBadgeClass"
              [matMenuTriggerFor]="statusMenu"
              [disabled]="updatingOrderStatus"
              >
              @if (updatingOrderStatus) {
                <mat-spinner diameter="14"></mat-spinner>
              }
              @if (!updatingOrderStatus) {
                <mat-icon class="!text-[16px] !h-4 !w-4">{{ orderStatusIcon }}</mat-icon>
              }
              Order Status: {{ order.orderStatus }}
              <mat-icon class="!text-[16px] !h-4 !w-4">expand_more</mat-icon>
            </button>
            <mat-menu #statusMenu="matMenu">
              <!-- Disabled buttons don't receive hover events, so the
              tooltip sits on a wrapping span instead of the button
              itself, or it would never show. -->
              <span [matTooltip]="canCloseOut ? '' : 'Only available once every item on this order has been checked off'" matTooltipPosition="left">
                <button mat-menu-item [disabled]="!canCloseOut" (click)="openStatusPrompt()">
                  <mat-icon>check_circle</mat-icon>
                  <span>Mark as Completed</span>
                </button>
              </span>
              <span [matTooltip]="canCloseOut ? '' : 'Only available once every item on this order has been checked off'" matTooltipPosition="left">
                <button mat-menu-item [disabled]="!canCloseOut" (click)="openStatusPrompt()">
                  <mat-icon>report_problem</mat-icon>
                  <span>Mark as Delayed Dispatch</span>
                </button>
              </span>
              <button mat-menu-item (click)="openVoidPrompt()">
                <mat-icon>block</mat-icon>
                <span>Mark as Void</span>
              </button>
            </mat-menu>
            <button
              class="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold border transition-all"
              [ngClass]="dispatchGroupOption ? dispatchGroupOption!.badgeClass + ' border-transparent' : 'border-dashed border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-600'"
              [matMenuTriggerFor]="groupMenu"
              >
              <mat-icon class="!text-[16px] !h-4 !w-4">{{ dispatchGroupOption?.icon || 'label' }}</mat-icon>
              {{ dispatchGroupOption?.label || 'Set Dispatch Group' }}
              <mat-icon class="!text-[16px] !h-4 !w-4">expand_more</mat-icon>
            </button>
            @if (deliveryPartner) {
              <div class="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold bg-neutral-900 text-white">
                <mat-icon class="!text-[16px] !h-4 !w-4 text-amber-400">local_shipping</mat-icon>
                {{ deliveryPartner.name }}
                @if (deliveryPartner.trackingUrl) {
                  <a [href]="deliveryPartner.trackingUrl" target="_blank" rel="noopener" class="text-amber-400 hover:text-amber-300 hover:no-underline" matTooltip="Open tracking"><mat-icon class="!text-[15px] !h-[15px] !w-[15px]">open_in_new</mat-icon></a>
                }
              </div>
            }
            <mat-menu #groupMenu="matMenu">
              @for (g of dispatchGroupOptions; track g) {
                <button mat-menu-item (click)="assignDispatchGroup(g.value)">
                  <mat-icon>{{ g.icon }}</mat-icon>
                  <span>{{ g.label }}</span>
                </button>
              }
            </mat-menu>
            @if (!isReadOnly) {
              <button
                class="flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 font-semibold text-sm shadow-sm hover:shadow-md hover:from-amber-300 hover:to-amber-400 active:scale-[0.98] transition-all"
                (click)="validateAllMatching()"
                matTooltip="Mark every unvalidated item as matching what was ordered"
                >
                <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">done_all</mat-icon>
                Validate All Matching
              </button>
            }
            @if (!isReadOnly) {
              <button
                class="flex items-center gap-2 px-5 py-3 rounded-xl border border-gray-200 bg-white text-gray-700 font-semibold text-sm shadow-sm hover:border-amber-300 hover:text-neutral-900 active:scale-[0.98] transition-all"
                (click)="openAddLine()"
                matTooltip="Send a substitute product in place of an ordered item"
                >
                <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">swap_horiz</mat-icon>
                Add Delivery Line
              </button>
            }
          </div>
        </div>
        <!-- Progress -->
        <div class="mt-6">
          <div class="flex flex-row justify-between items-center mb-2">
            <div class="flex items-center gap-x-4 text-xs font-medium">
              <span class="flex items-center gap-1.5 text-green-600">
                <span class="h-2 w-2 rounded-full bg-green-500"></span>
                {{ validatedCount }} validated
              </span>
              @if (discrepancyCount) {
                <span class="flex items-center gap-1.5 text-red-500">
                  <span class="h-2 w-2 rounded-full bg-red-500"></span>
                  {{ discrepancyCount }} discrepancy
                </span>
              }
              @if (cancelledCount) {
                <span class="flex items-center gap-1.5 text-neutral-500">
                  <span class="h-2 w-2 rounded-full bg-neutral-500"></span>
                  {{ cancelledCount }} cancelled
                </span>
              }
              <span class="flex items-center gap-1.5 text-gray-400">
                <span class="h-2 w-2 rounded-full bg-gray-300"></span>
                {{ pendingCount }} pending
              </span>
            </div>
            <span class="text-xs font-semibold text-gray-500">{{ progressPercent }}%</span>
          </div>
          <div class="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
            <div
              class="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all duration-500"
              [style.width.%]="progressPercent"
            ></div>
          </div>
        </div>
      </div>
      <!-- What to know before packing: where it's going, what the customer asked for, what was ordered -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6 items-start">
        <!-- Delivery address -->
        @if (shippingAddress) {
          <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div class="relative bg-gradient-to-br from-neutral-900 to-neutral-700 px-5 py-4 text-white">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-amber-400">
                  <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">local_shipping</mat-icon> Deliver to
                </div>
                @if (order.deliveryMethod) {
                  <span class="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white/10 text-white/80">{{ deliveryMethodLabel }}</span>
                }
              </div>
              <div class="mt-2 text-xl font-bold leading-tight">{{ customerName }}</div>
              @if (shippingAddress.companyName) {
                <div class="text-sm text-white/70">{{ shippingAddress.companyName }}</div>
              }
              @if (shippingAddress.postCode) {
                <div class="mt-3 flex items-center gap-2">
                  <span class="text-base font-extrabold tracking-wider px-3 py-1 rounded-lg bg-amber-400 text-neutral-900 shadow-sm">{{ shippingAddress.postCode }}</span>
                  @if (shippingAddress.country) {
                    <span class="text-xs text-white/60">{{ shippingAddress.country }}</span>
                  }
                </div>
              }
            </div>
            <div class="px-5 pt-4 pb-3 text-sm text-gray-700 leading-relaxed">
              <div class="font-medium text-gray-900">{{ shippingAddress.address1 }}</div>
              @if (shippingAddress.address2) {
                <div>{{ shippingAddress.address2 }}</div>
              }
              <div>{{ shippingAddress.city }}</div>
            </div>
            @if (shippingAddress.phoneNo || shippingAddress.email) {
              <div class="px-5 pb-4 flex flex-col gap-1.5">
                @if (shippingAddress.phoneNo) {
                  <div class="flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2">
                    <mat-icon class="!text-[17px] !h-[17px] !w-[17px] !text-gray-400">call</mat-icon>
                    <a class="flex-1 text-sm font-medium text-gray-800 hover:text-amber-700" [href]="'tel:' + shippingAddress.phoneNo">{{ shippingAddress.phoneNo }}</a>
                    <button class="h-7 w-7 rounded-full text-gray-400 hover:bg-white hover:text-gray-800 flex items-center justify-center transition-colors" (click)="copyText(shippingAddress.phoneNo, 'Phone number')" matTooltip="Copy phone number">
                      <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">content_copy</mat-icon>
                    </button>
                  </div>
                }
                @if (shippingAddress.email) {
                  <div class="flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2">
                    <mat-icon class="!text-[17px] !h-[17px] !w-[17px] !text-gray-400">mail</mat-icon>
                    <a class="flex-1 text-sm font-medium text-gray-800 hover:text-amber-700 break-all" [href]="'mailto:' + shippingAddress.email">{{ shippingAddress.email }}</a>
                    <button class="h-7 w-7 rounded-full text-gray-400 hover:bg-white hover:text-gray-800 flex items-center justify-center transition-colors" (click)="copyText(shippingAddress.email, 'Email')" matTooltip="Copy email">
                      <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">content_copy</mat-icon>
                    </button>
                  </div>
                }
              </div>
            }
            <div class="grid grid-cols-2 border-t border-gray-100">
              <button class="flex items-center justify-center gap-1.5 py-3 text-xs font-semibold text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors border-r border-gray-100" (click)="copyText(addressText(shippingAddress), 'Address')">
                <mat-icon class="!text-[16px] !h-4 !w-4">content_copy</mat-icon> Copy address
              </button>
              <a class="flex items-center justify-center gap-1.5 py-3 text-xs font-semibold text-amber-700 hover:bg-amber-50 transition-colors" [href]="mapsUrl(shippingAddress)" target="_blank" rel="noopener">
                <mat-icon class="!text-[16px] !h-4 !w-4">map</mat-icon> Open in Maps
              </a>
            </div>
          </div>
        }
        <!-- Customer notes: instructions to read before dispatch -->
        @if (order.orderNotes) {
          <div class="relative rounded-2xl overflow-hidden border border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50 shadow-sm">
            <div class="absolute left-0 top-0 bottom-0 w-1.5 bg-amber-400"></div>
            <div class="pl-6 pr-5 py-5">
              <div class="flex items-center justify-between gap-2">
                <div class="flex items-center gap-2">
                  <span class="h-8 w-8 rounded-full bg-amber-400 text-neutral-900 flex items-center justify-center">
                    <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">priority_high</mat-icon>
                  </span>
                  <div class="text-xs font-bold text-amber-800 uppercase tracking-widest">Customer notes</div>
                </div>
                <span class="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-amber-200/70 text-amber-900">Read before dispatch</span>
              </div>
              <div class="mt-4 flex gap-2">
                <span class="text-4xl leading-none font-serif text-amber-300 select-none">&ldquo;</span>
                <div class="text-base font-medium text-amber-950 whitespace-pre-line leading-relaxed pt-1.5">{{ order.orderNotes }}</div>
              </div>
            </div>
          </div>
        } @else {
          <div class="rounded-2xl border border-dashed border-gray-200 bg-white px-5 py-5 flex items-center gap-3">
            <span class="h-8 w-8 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center flex-shrink-0">
              <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">check</mat-icon>
            </span>
            <div>
              <div class="text-xs font-bold text-gray-400 uppercase tracking-widest">Customer notes</div>
              <div class="text-sm text-gray-400 mt-0.5">The customer left no notes on this order.</div>
            </div>
          </div>
        }
        <!-- Original order - always shown as ordered, regardless of splits,
        substitutes or cancellations on the delivery lines -->
        @if (order.OrderItems?.length) {
          <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div class="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div class="flex items-center gap-2">
                <span class="h-8 w-8 rounded-lg bg-gray-50 flex items-center justify-center">
                  <mat-icon class="!text-gray-400 !text-[18px] !h-[18px] !w-[18px]">receipt_long</mat-icon>
                </span>
                <div class="font-semibold text-gray-800 text-sm">Original order</div>
              </div>
              <span class="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-500">As ordered</span>
            </div>
            <div class="flex flex-col divide-y divide-gray-50 max-h-72 overflow-y-auto">
              @for (item of order.OrderItems; track item; let i = $index) {
                <div class="flex items-start gap-3 px-5 py-3.5">
                  <span class="h-6 w-6 rounded-full bg-gray-100 text-[11px] font-bold text-gray-500 flex items-center justify-center flex-shrink-0 mt-0.5">{{ i + 1 }}</span>
                  <div class="min-w-0 flex-1">
                    <div class="text-sm font-semibold text-gray-900 leading-snug">{{ item.productName }}</div>
                    <div class="text-[11px] text-gray-400 font-mono mt-0.5 break-all">{{ item.productSku }}</div>
                    @if (itemProgress(item); as p) {
                      <span class="inline-flex items-center gap-1 mt-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full"
                        [ngClass]="p.state === 'done' ? 'bg-green-50 text-green-700' : p.state === 'partial' ? 'bg-amber-50 text-amber-700' : p.state === 'cancelled' ? 'bg-neutral-100 text-neutral-600' : 'bg-gray-100 text-gray-500'">
                        <mat-icon class="!text-[12px] !h-3 !w-3">{{ p.state === 'done' ? 'check_circle' : p.state === 'cancelled' ? 'cancel' : p.state === 'partial' ? 'timelapse' : 'schedule' }}</mat-icon>
                        {{ p.label }}
                      </span>
                    }
                  </div>
                  <div class="flex-shrink-0 text-right">
                    <div class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Qty</div>
                    <div class="text-lg font-extrabold text-gray-900 leading-none">{{ item.quantity }}</div>
                  </div>
                </div>
              }
            </div>
            <div class="flex items-center justify-between px-5 py-3 bg-gray-50 border-t border-gray-100 text-xs font-semibold text-gray-500">
              <span>{{ order.OrderItems.length }} line{{ order.OrderItems.length === 1 ? '' : 's' }}</span>
              <span>{{ originalUnits }} unit{{ originalUnits === 1 ? '' : 's' }} in total</span>
            </div>
          </div>
        }
      </div>
      <!-- Items -->
      <div class="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
        <div class="xl:col-span-2 flex flex-col gap-y-3 min-w-0">
          @for (row of rows; track row) {
            <div
              class="bg-white rounded-2xl border shadow-sm p-5 transition-all"
          [ngClass]="{
            'border-green-200 ring-1 ring-green-100': row.validation.status === 'VALIDATED',
            'border-red-200 ring-1 ring-red-100': row.validation.status === 'DISCREPANCY',
            'border-gray-200 bg-gray-50 opacity-75': row.validation.status === 'CANCELLED',
            'border-gray-100': row.validation.status === 'PENDING'
          }"
              >
              <div class="flex flex-row items-start justify-between gap-x-6 flex-wrap gap-y-4">
                <!-- Product info -->
                <div class="min-w-[220px] flex-1 basis-[260px]">
                  <div class="font-semibold text-gray-900">{{ rowProductName(row) }}</div>
                  <div class="text-xs text-gray-400 font-mono mt-1">{{ rowProductSku(row) }}</div>
                  @if (row.validation.splitType === 'SUBSTITUTE') {
                    <div class="text-xs text-blue-600 mt-1">
                      Instead of {{ row.item.productName }}
                    </div>
                  }
                  <div class="mt-2 flex flex-wrap items-center gap-1.5">
                    <span class="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 bg-gray-50 px-2.5 py-1 rounded-full">
                      Ordered: {{ row.validation.expectedQty }}
                    </span>
                    @if (row.validation.splitType) {
                      <span
                        class="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full"
                        >
                        {{ row.validation.splitType === 'REPLACEMENT' ? 'Replacement' : row.validation.splitType === 'SUBSTITUTE' ? 'Substitute' : 'Remaining' }} line
                      </span>
                    }
                    @if (row.dimensions?.weight) {
                      <span
                        class="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-50 px-2.5 py-1 rounded-full"
                        >
                        <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">scale</mat-icon>
                        {{ row.dimensions?.weight }}kg
                      </span>
                    }
                    @if (row.dimensions?.length || row.dimensions?.width || row.dimensions?.height) {
                      <span
                        class="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-50 px-2.5 py-1 rounded-full"
                        >
                        <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">straighten</mat-icon>
                        {{ row.dimensions?.length || '-' }} × {{ row.dimensions?.width || '-' }} × {{ row.dimensions?.height || '-' }}
                      </span>
                    }
                    <button
                      type="button"
                      class="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 px-2.5 py-1 rounded-full transition-colors"
                      (click)="openStockHistory(row)"
                      matTooltip="See every stock movement for this product"
                      >
                      <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">history</mat-icon>
                      Stock history
                    </button>
                  </div>
                </div>
                <!-- Qty stepper -->
                @if (!rowReadOnly(row)) {
                  <div>
                    <div class="text-xs text-gray-400 uppercase tracking-wide mb-1.5">Checked Qty</div>
                    <div
                      class="flex items-center rounded-xl border border-gray-200 overflow-hidden w-32"
                      [ngClass]="{ 'opacity-40 pointer-events-none': row.editCancel }"
                      >
                      <button
                        class="flex items-center justify-center h-10 w-9 text-gray-400 hover:bg-gray-50 hover:text-gray-700 transition-colors"
                        (click)="adjustQty(row, -1)"
                        >
                        <mat-icon class="!text-[18px]">remove</mat-icon>
                      </button>
                      <input
                        type="number"
                        class="flex-1 text-center outline-none text-sm font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        [ngModel]="row.editValidatedQty"
                        (ngModelChange)="onQtyChange(row, $event)"
                        min="0"
                        />
                      <button
                        class="flex items-center justify-center h-10 w-9 text-gray-400 hover:bg-gray-50 hover:text-gray-700 transition-colors"
                        (click)="adjustQty(row, 1)"
                        >
                        <mat-icon class="!text-[18px]">add</mat-icon>
                      </button>
                    </div>
                    <button
                      type="button"
                      class="mt-1.5 text-xs font-semibold text-amber-700 hover:text-amber-900 underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
                      [disabled]="row.editCancel || (row.editValidatedQty === row.validation.expectedQty && row.editCondition === 'GOOD')"
                      (click)="matchRow(row)"
                      matTooltip="Set the checked quantity to what was ordered, in good condition"
                      >
                      Match ordered ({{ row.validation.expectedQty }})
                    </button>
                  </div>
                }
                @if (rowReadOnly(row)) {
                  <div>
                    <div class="text-xs text-gray-400 uppercase tracking-wide mb-1.5">Checked Qty</div>
                    <div class="text-sm font-semibold text-gray-800">{{ row.validation.validatedQty ?? '-' }}</div>
                  </div>
                }
                <!-- Condition -->
                @if (!rowReadOnly(row)) {
                  <div>
                    <div class="text-xs text-gray-400 uppercase tracking-wide mb-1.5">Condition</div>
                    <div class="flex items-center gap-1.5">
                      @for (c of conditionOptions; track c) {
                        <button
                          class="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold border transition-all"
                  [ngClass]="{
                    'bg-green-500 border-green-500 text-white': !row.editCancel && row.editCondition === c.value && c.value === 'GOOD',
                    'bg-red-500 border-red-500 text-white': !row.editCancel && row.editCondition === c.value && c.value === 'DAMAGED',
                    'bg-amber-400 border-amber-400 text-neutral-900': !row.editCancel && row.editCondition === c.value && c.value === 'PARTIAL',
                    'bg-white border-gray-200 text-gray-500 hover:border-gray-300': row.editCancel || row.editCondition !== c.value
                  }"
                          (click)="setCondition(row, c.value)"
                          >
                          <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">{{ c.icon }}</mat-icon>
                          {{ c.label }}
                        </button>
                      }
                      <button
                        class="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold border transition-all"
                        [ngClass]="row.editCancel ? 'bg-neutral-700 border-neutral-700 text-white' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'"
                        (click)="toggleCancel(row)"
                        matTooltip="Item won't ship - takes effect on Save"
                        >
                        <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">cancel</mat-icon>
                        Cancel
                      </button>
                    </div>
                  </div>
                }
                @if (rowReadOnly(row)) {
                  <div>
                    <div class="text-xs text-gray-400 uppercase tracking-wide mb-1.5">Condition</div>
                    <div class="text-sm font-semibold text-gray-800">{{ row.validation.condition || '-' }}</div>
                  </div>
                }
                <!-- Notes -->
                @if (!rowReadOnly(row)) {
                  <div class="flex-1 min-w-[120px]">
                    <div class="text-xs text-gray-400 uppercase tracking-wide mb-1.5">Notes</div>
                    <input
                      type="text"
                      class="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all"
                      [(ngModel)]="row.editNotes"
                      placeholder="Optional"
                      />
                  </div>
                }
                @if (rowReadOnly(row) && row.validation.notes) {
                  <div class="flex-1 min-w-[160px]">
                    <div class="text-xs text-gray-400 uppercase tracking-wide mb-1.5">Notes</div>
                    <div class="text-sm text-gray-800">{{ row.validation.notes }}</div>
                  </div>
                }
                <!-- Status + Save -->
                <div class="flex flex-col items-end gap-y-2 flex-shrink-0">
                  <span
                    class="px-2.5 py-1 rounded-full text-xs font-semibold"
                [ngClass]="{
                  'bg-green-100 text-green-700': row.validation.status === 'VALIDATED',
                  'bg-red-100 text-red-700': row.validation.status === 'DISCREPANCY' || row.validation.status === 'DAMAGED',
                  'bg-neutral-200 text-neutral-600': row.validation.status === 'CANCELLED',
                  'bg-amber-100 text-amber-700': row.validation.status === 'PARTIAL',
                  'bg-gray-100 text-gray-500': row.validation.status === 'PENDING'
                }"
                    >
                    {{ row.validation.status }}
                  </span>
                  @if (row.validation.validatedBy) {
                    <div class="text-right">
                      @if (row.validation.validatedByName && row.validation.validatedByName !== row.validation.validatedBy) {
                        <div class="text-xs font-medium text-gray-600">
                          {{ row.validation.validatedByName }}
                        </div>
                      }
                      <div class="text-xs text-gray-400">{{ row.validation.validatedBy }}</div>
                    </div>
                  }
                  @if (!rowReadOnly(row)) {
                    <button
                      class="flex items-center justify-center h-9 w-9 rounded-full transition-all"
                [ngClass]="
                  row.justSaved
                    ? 'bg-green-500 text-white'
                    : rowDirty(row)
                      ? 'bg-neutral-900 text-amber-400 hover:bg-neutral-800'
                      : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
                "
                      (click)="saveRow(row)"
                      [disabled]="row.saving || !rowDirty(row)"
                      [matTooltip]="rowDirty(row) ? 'Save' : 'Nothing to save'"
                      >
                      @if (!row.saving && !row.justSaved) {
                        <mat-icon class="!text-[18px]">check</mat-icon>
                      }
                      @if (row.justSaved) {
                        <mat-icon class="!text-[18px]">done</mat-icon>
                      }
                      @if (row.saving) {
                        <mat-spinner diameter="16" class="!text-amber-400"></mat-spinner>
                      }
                    </button>
                  }
                </div>
              </div>
            </div>
          }
        </div>
        <!-- Delivery notes (and a quick way to add one) stay beside the lines -->
        <div class="flex flex-col gap-6 xl:sticky xl:top-20">
          <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div class="flex items-center gap-2 mb-3">
              <mat-icon class="!text-amber-500 !text-[18px] !h-[18px] !w-[18px]">sticky_note_2</mat-icon>
              <div class="font-semibold text-gray-800 text-sm">Delivery notes</div>
              @if (deliveryNotes.length) {
                <span class="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{{ deliveryNotes.length }}</span>
              }
            </div>
            @if (deliveryNotes.length) {
              <div class="flex flex-col gap-3 mb-4">
                @for (n of deliveryNotes; track n) {
                  <div class="border-l-2 border-amber-300 pl-3">
                    <div class="text-sm text-gray-800 whitespace-pre-line">{{ n.text }}</div>
                    <div class="text-xs text-gray-400 mt-1">{{ n.date | date: 'dd MMM yyyy, HH:mm' }}@if (n.user) {
                      <span> - {{ n.user }}</span>
                    }</div>
                  </div>
                }
              </div>
            } @else {
              <div class="text-xs text-gray-400 mb-3">No delivery notes yet.</div>
            }
            <textarea
              class="w-full px-3 py-2 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all resize-none"
              rows="2"
              [(ngModel)]="newDeliveryNote"
              placeholder="e.g. Left with neighbour at no. 14, customer called ahead..."
            ></textarea>
            <div class="mt-2 flex justify-end">
              <button
                class="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-neutral-900 text-amber-400 text-xs font-semibold hover:bg-neutral-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                [disabled]="!newDeliveryNote.trim() || savingDeliveryNote"
                (click)="addDeliveryNote()"
                >
                @if (savingDeliveryNote) {
                  <mat-spinner diameter="14"></mat-spinner>
                }
                @if (!savingDeliveryNote) {
                  <mat-icon class="!text-[16px] !h-4 !w-4">add</mat-icon>
                }
                Add note
              </button>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Status prompt - shown when saving would validate the last outstanding item(s); nothing is saved unless a status is chosen -->
    @if (showStatusPrompt) {
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 modal-backdrop">
        <div class="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 modal-card">
          <div class="flex items-center gap-3 mb-2">
            <div class="flex items-center justify-center h-10 w-10 rounded-full bg-green-100 text-green-600 flex-shrink-0">
              <mat-icon class="!text-[22px]">check_circle</mat-icon>
            </div>
            <div class="font-bold text-gray-900 text-lg">Close out this order?</div>
          </div>
          @if (closeOutRows.length) {
            <p class="text-sm text-gray-500 mb-4">
              Validating {{ closeOutRows.length === 1 ? 'this item' : closeOutRows.length + ' items' }} completes order #{{ order.orderNo }}. Choose the order status to save it with - "Not now" leaves {{ closeOutRows.length === 1 ? 'the item' : 'the items' }} unvalidated.
            </p>
          }
          @if (!closeOutRows.length) {
            <p class="text-sm text-gray-500 mb-4">
              Choose the new status for order #{{ order.orderNo }}.
            </p>
          }
          <div class="mb-4">
            <div class="flex items-center justify-between mb-1.5">
              <div class="text-xs text-gray-400 uppercase tracking-wide">Delivery partner{{ partnerRequired ? '' : ' (optional for collection)' }}</div>
              @if (partnerRequired && selectedPartnerId == null && partners.length) {
                <span class="text-[11px] font-semibold text-amber-600">Required</span>
              }
            </div>
            @if (partnersLoading) {
              <div class="flex items-center gap-2 text-sm text-gray-400 py-2"><mat-spinner diameter="16"></mat-spinner> Loading partners…</div>
            } @else if (partnersError) {
              <div class="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{{ partnersError }}</div>
            } @else if (!partners.length) {
              <div class="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
                No delivery partners set up yet - an admin can add them under Delivery &gt; Delivery Partners.
              </div>
            } @else {
              <div class="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                @for (p of partners; track p.id) {
                  <button type="button"
                    class="flex items-center gap-2 px-3 py-2 rounded-xl border text-left text-sm transition-colors"
                    [ngClass]="selectedPartnerId === p.id ? 'border-neutral-900 bg-neutral-900 text-white' : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'"
                    (click)="selectedPartnerId = selectedPartnerId === p.id && !partnerRequired ? null : p.id!">
                    <mat-icon class="!text-[18px] !h-[18px] !w-[18px] flex-shrink-0" [ngClass]="selectedPartnerId === p.id ? 'text-amber-400' : 'text-gray-400'">{{ selectedPartnerId === p.id ? 'check_circle' : 'local_shipping' }}</mat-icon>
                    <span class="truncate font-medium">{{ p.name }}</span>
                  </button>
                }
              </div>
            }
          </div>
          <div class="mb-4">
            <div class="text-xs text-gray-400 uppercase tracking-wide mb-1.5">Note (optional)</div>
            <textarea
              class="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-green-400 focus:ring-2 focus:ring-green-100 transition-all resize-none"
              rows="2"
              [(ngModel)]="statusNote"
              placeholder="Anything worth recording about this dispatch?"
            ></textarea>
          </div>
          <label class="mb-4 flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer select-none transition-colors"
            [ngClass]="emailCustomer ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'">
            <input type="checkbox" class="h-4 w-4 accent-green-600 cursor-pointer" [(ngModel)]="emailCustomer" />
            <mat-icon class="!text-[18px] !h-[18px] !w-[18px]" [ngClass]="emailCustomer ? 'text-green-600' : 'text-gray-400'">{{ emailCustomer ? 'mark_email_read' : 'unsubscribe' }}</mat-icon>
            <span class="text-sm">
              <span class="font-semibold text-gray-900">Email the customer</span>
              <span class="block text-xs text-gray-500">{{ emailCustomer ? 'They\'ll get the "Your order is ready" email.' : 'No email will be sent.' }}</span>
            </span>
          </label>
          <div class="flex flex-col gap-2">
            <button
              class="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-green-600 text-white font-semibold text-sm hover:bg-green-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              [disabled]="!canConfirmCloseOut"
              (click)="confirmStatusFromPrompt('COMPLETED')"
              >
              <mat-icon class="!text-[18px]">check_circle</mat-icon>
              Mark as Completed
            </button>
            <button
              class="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-orange-100 text-orange-700 font-semibold text-sm hover:bg-orange-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              [disabled]="!canConfirmCloseOut"
              (click)="confirmStatusFromPrompt('DELAYED COMPLETION')"
              >
              <mat-icon class="!text-[18px]">report_problem</mat-icon>
              Mark as Delayed Dispatch
            </button>
            <button
              class="px-4 py-2 rounded-xl text-gray-500 hover:text-gray-800 hover:bg-gray-50 font-semibold text-sm transition-colors"
              (click)="dismissStatusPrompt()"
              >
              {{ closeOutRows.length ? 'Not now' : 'Cancel' }}
            </button>
          </div>
        </div>
      </div>
    }


    <!-- Add delivery line (substitute product) -->
    @if (showAddLine) {
      <div
        class="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/50 px-4 modal-backdrop"
        (click)="dismissPendingSave()"
        >
        <div class="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden modal-card" (click)="$event.stopPropagation()">
          <div class="h-1.5 w-full bg-gradient-to-r from-amber-300 to-amber-500"></div>
          <div class="p-7 max-h-[85vh] overflow-y-auto">
            <div class="flex items-start gap-4">
              <div class="flex items-center justify-center h-12 w-12 rounded-2xl bg-amber-50 text-amber-600 flex-shrink-0">
                <mat-icon class="!text-[26px] !h-[26px] !w-[26px]">swap_horiz</mat-icon>
              </div>
              <div>
                <div class="font-bold text-gray-900 text-lg leading-tight">Add delivery line</div>
                <div class="text-sm text-gray-500 mt-1">
                  Send a different product in place of an ordered item, e.g. when it's out of stock and the customer agreed to a substitute.
                </div>
              </div>
            </div>
            <div class="mt-6">
              <div class="text-xs text-gray-400 uppercase tracking-wide mb-1.5">Replacing</div>
              <select
                class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                [(ngModel)]="addLineKey"
                (ngModelChange)="onAddLineRowChange()"
                >
                @for (o of replaceOptions; track o) {
                  <option [ngValue]="o.key">{{ o.label }}</option>
                }
              </select>
            </div>
            @if (addLineOption; as ao) {
              <div class="mt-4">
                @if (ao.lineId) {
                  <div class="text-xs text-gray-400 uppercase tracking-wide mb-1.5">
                    Units being replaced (0 - {{ ao.pending }})
                  </div>
                  <input
                    type="number"
                    min="0"
                    [max]="ao.pending"
                    class="w-32 px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm font-semibold outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                    [(ngModel)]="replacedQty"
                    />
                  <div class="text-xs text-gray-400 mt-1.5">Use 0 to add the substitute without cancelling any of the original.</div>
                } @else {
                  <div class="text-xs text-gray-500 bg-gray-50 rounded-xl px-3 py-2.5">
                    Nothing is pending on this item, so the original line is left as it is and the substitute is simply added.
                  </div>
                }
              </div>
            }
            <div class="mt-4 relative">
              <div class="text-xs text-gray-400 uppercase tracking-wide mb-1.5">Substitute product</div>
              <app-product-picker
                placeholder="Search by name, SKU or barcode"
                [products]="pickerProducts"
                [variantOnly]="true"
                [showAllOnEmpty]="false"
                [suggestions]="subSuggestions"
                suggestionsLabel="Similar products"
                stockYard="M"
                [selectedId]="subProduct?.productId ?? null"
                [(query)]="subSearch"
                (queryEdited)="onSubSearchInput()"
                (picked)="selectSubProduct($event)"
                />
            </div>
            @if (subProduct) {
              <div class="mt-4 flex flex-row gap-4">
                <div>
                  <div class="text-xs text-gray-400 uppercase tracking-wide mb-1.5">Quantity to send</div>
                  <input
                    type="number"
                    min="1"
                    class="w-28 px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm font-semibold outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                    [(ngModel)]="subQty"
                    />
                </div>
                <div class="flex-1">
                  <div class="text-xs text-gray-400 uppercase tracking-wide mb-1.5">Take stock from</div>
                  <select
                    class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                    [(ngModel)]="subWarehouse"
                    >
                    <option value="Markstey">Markstey ({{ subProduct.mStockQty ?? 0 }} in stock)</option>
                    <option value="Kettering">Kettering ({{ subProduct.kStockQty ?? 0 }} in stock)</option>
                  </select>
                </div>
              </div>
            }
            <div class="mt-4">
              <div class="text-xs text-gray-400 uppercase tracking-wide mb-1.5">Note (optional)</div>
              <input
                type="text"
                class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                placeholder="e.g. Customer agreed by phone"
                [(ngModel)]="subNotes"
                />
            </div>
            @if (addLineOption && subProduct) {
              <ul class="mt-5 flex flex-col gap-2 text-sm text-gray-600">
                @if (addLineOption.lineId && replacedQty > 0) {
                  <li class="flex items-start gap-2.5">
                    <mat-icon class="!text-[18px] !h-[18px] !w-[18px] mt-0.5 text-gray-400">inventory_2</mat-icon>
                    <span>{{ replacedQty }} of {{ addLineOption.name }} go back into stock (line cancelled).</span>
                  </li>
                }
                <li class="flex items-start gap-2.5">
                  <mat-icon class="!text-[18px] !h-[18px] !w-[18px] mt-0.5 text-gray-400">local_shipping</mat-icon>
                  <span>{{ subQty || 0 }} of {{ subProduct.productName }} come off stock at {{ subWarehouse }}.</span>
                </li>
              </ul>
            }
            <div class="flex flex-row gap-2 mt-7">
              <button
                class="flex-1 px-4 py-3 rounded-xl bg-neutral-900 text-amber-400 font-semibold text-sm hover:bg-neutral-800 shadow-md shadow-neutral-300 transition-all active:scale-[0.98] disabled:opacity-50"
                [disabled]="addingLine"
                (click)="confirmAddLine()"
                >
                {{ addingLine ? 'Adding...' : 'Add substitute line' }}
              </button>
              <button
                class="px-5 py-3 rounded-xl text-gray-500 hover:text-gray-800 hover:bg-gray-100 font-semibold text-sm transition-colors"
                (click)="dismissPendingSave()"
                >
                Go back
              </button>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Save confirmation - Damaged / Partial / Cancel -->
    @if (pendingConfirmation; as c) {
      <div
        class="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/50 px-4 modal-backdrop"
        (click)="dismissPendingSave()"
        >
        <div
          class="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden modal-card"
          (click)="$event.stopPropagation()"
          >
          <div
            class="h-1.5 w-full"
          [ngClass]="{
            'bg-gradient-to-r from-red-400 to-red-600': c.tone === 'red',
            'bg-gradient-to-r from-amber-300 to-amber-500': c.tone === 'amber',
            'bg-gradient-to-r from-neutral-500 to-neutral-700': c.tone === 'neutral'
          }"
          ></div>
          <div class="p-7">
            <div class="flex items-start gap-4">
              <div
                class="flex items-center justify-center h-12 w-12 rounded-2xl flex-shrink-0"
              [ngClass]="{
                'bg-red-50 text-red-600': c.tone === 'red',
                'bg-amber-50 text-amber-600': c.tone === 'amber',
                'bg-neutral-100 text-neutral-700': c.tone === 'neutral'
              }"
                >
                <mat-icon class="!text-[26px] !h-[26px] !w-[26px]">{{ c.icon }}</mat-icon>
              </div>
              <div class="min-w-0">
                <div class="font-bold text-gray-900 text-lg leading-tight">{{ c.title }}</div>
                <div class="text-sm text-gray-500 mt-1 truncate">{{ rowProductName(c.row) }}</div>
                <div class="text-xs text-gray-400 font-mono truncate">{{ rowProductSku(c.row) }}</div>
              </div>
            </div>
            <ul class="mt-6 flex flex-col gap-3">
              @for (e of c.effects; track e) {
                <li class="flex items-start gap-3 text-sm text-gray-700">
                  <mat-icon class="!text-[18px] !h-[18px] !w-[18px] mt-0.5 text-gray-400 flex-shrink-0">{{ e.icon }}</mat-icon>
                  <span>{{ e.text }}</span>
                </li>
              }
            </ul>
            <div class="flex flex-row gap-2 mt-7">
              <button
                class="flex-1 px-4 py-3 rounded-xl font-semibold text-sm transition-all active:scale-[0.98]"
              [ngClass]="{
                'bg-red-600 text-white hover:bg-red-700 shadow-md shadow-red-200': c.tone === 'red',
                'bg-neutral-900 text-amber-400 hover:bg-neutral-800 shadow-md shadow-neutral-300': c.tone !== 'red'
              }"
                (click)="confirmPendingSave()"
                >
                {{ c.actionLabel }}
              </button>
              <button
                class="px-5 py-3 rounded-xl text-gray-500 hover:text-gray-800 hover:bg-gray-100 font-semibold text-sm transition-colors"
                (click)="dismissPendingSave()"
                >
                Go back
              </button>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Void reason prompt -->
    @if (showVoidPrompt) {
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 modal-backdrop">
        <div class="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 modal-card">
          <div class="flex items-center gap-3 mb-2">
            <div class="flex items-center justify-center h-10 w-10 rounded-full bg-red-100 text-red-600 flex-shrink-0">
              <mat-icon class="!text-[22px]">block</mat-icon>
            </div>
            <div class="font-bold text-gray-900 text-lg">Void order #{{ order.orderNo }}</div>
          </div>
          <p class="text-sm text-gray-500 mb-3">A reason is required and is recorded as an order note.</p>
          <textarea
            class="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all resize-none"
            rows="3"
            [(ngModel)]="voidReason"
            placeholder="Why is this order being voided?"
          ></textarea>
          <div class="flex flex-row gap-2 mt-4">
            <button
              class="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 text-white font-semibold text-sm hover:bg-red-700 transition-colors"
              (click)="confirmVoid()"
              >
              <mat-icon class="!text-[18px]">block</mat-icon>
              Void Order
            </button>
            <button
              class="px-4 py-2.5 rounded-xl text-gray-500 hover:text-gray-800 hover:bg-gray-50 font-semibold text-sm transition-colors"
              (click)="cancelVoidPrompt()"
              >
              Cancel
            </button>
          </div>
        </div>
      </div>
    }
  </div>
</app-main-layout>
